import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { usePostHog } from "../../common/posthog";
import { resetPostHogIdentity } from "../../common/posthog/posthog-client";
import { supabase } from "../../data/supabase/client";

const ResetPassword = () => {
  const navigate = useNavigate();
  const posthog = usePostHog();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      await supabase.auth.signOut();
    })();
    resetPostHogIdentity(posthog);
  }, [posthog]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    setError(null);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email);

      if (error) {
        throw error;
      }

      setSuccessMessage("A 6-digit verification code has been sent to your email address");

      navigate(`/verify-otp?email=${encodeURIComponent(email)}&type=recovery`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred during password reset");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default"
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: "100%",
          maxWidth: 400,
          display: "flex",
          flexDirection: "column",
          gap: 2
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Reset Password
        </Typography>

        <Typography variant="body2" color="text.secondary" align="center" gutterBottom>
          Enter your email to receive a verification code for password reset
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={email}
              onChange={handleInputChange}
              required
              disabled={isLoading}
            />

            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={isLoading}
              sx={{ mt: 2 }}
            >
              {isLoading ? <CircularProgress size={24} /> : "Send Verification Code"}
            </Button>
          </Stack>
        </form>

        <Box mt={2} textAlign="center">
          <Link to="/login" style={{ textDecoration: "none" }}>
            <Typography color="primary" variant="body2">
              Back to login
            </Typography>
          </Link>
        </Box>
      </Paper>
    </Box>
  );
};

export default ResetPassword;
